<template>
  <slot
    v-bind="{
      deliveryAddress,
      isNewAddress,
      stepId,
      emailAddress,
      continueShoppingUrl,
      errorMessages,
      isRestrictedToRetailer,
    }"
  ></slot>
</template>
<script>
import {mapActions, mapGetters, mapMutations, mapState} from 'vuex';
import {useVuelidate} from '@vuelidate/core';

import {PdlToastType} from '@/constants/pdl-toast-type';
import {PaymentProvider} from '@/constants/payment-provider.js';
import {scrollToTop} from '@/utils/scroll-to.js';
import trackCheckout from '@/mixins/tracking/track-checkout.js';
import {useApplePaySdk} from '@/composables/apple-pay-sdk.js';
import {ShipmentsApi} from '@/api/shipments';
import {CheckoutStep} from '@/constants/checkout';
import {PersonalDetailsKeyMap, RequiredAddressKeyMap, CustomerDetailsFieldsKeyMap} from '@/constants/address';
import {focusElement} from '@/utils/accessibility/a11y-utils';
import {PdlHeading} from '@pedal/pdl-section-header';

const BILLING_DETAILS_EVENT = 'billingDetailsError';
const parseBillingErrorKey = (key) => {
  const billingErrorPrefix = 'billingAddress.';
  if (!key.includes(billingErrorPrefix)) return key;
  return key.split(billingErrorPrefix)?.[1] ?? key;
};

export default {
  name: 'CheckoutMultiStepContainer',
  components: [PdlHeading],

  mixins: [trackCheckout],
  provide() {
    return {
      validationErrors: this.validationErrors,
      setValidationErrors: this.setValidationErrors,
      clearValidationErrors: this.clearValidationErrors,
      handleCheckoutSubmitButtonAction: this.handleCheckoutSubmitButtonAction,
      cartDataFull: this.cartDataFull,
      shipmentInfos: this.shipmentInfos,
    };
  },

  props: {
    cartData: {
      type: Array,
      default: () => null,
    },
    cartDataFull: {
      type: Object,
      default: () => null,
    },
    productTypeDetails: {
      type: Object,
      default: () => null,
    },
    emailAddress: {
      type: String,
      default: '',
    },
    shipmentInfos: {
      type: Object,
      default: () => null,
    },
  },

  setup() {
    useApplePaySdk();
    const v$ = useVuelidate();
    return {v$};
  },

  data() {
    return {
      validationErrors: [],
    };
  },

  computed: {
    ...mapState('checkout', [
      'selectedDeliveryMode',
      'errorMessages',
      'selectedPaymentProvider',
      'gr4vyFormValidations',
      'continueShoppingUrl',
      'billingAddress',
    ]),
    ...mapGetters('checkout', ['isRestrictedToRetailer']),
    ...mapState('shipments', ['shipmentsInfo', 'entries', 'warehouses']),
    ...mapState('backend', ['isUserLoggedIn']),
    defaultDeliveryAddress() {
      return this.deliveryAddress.find((item) => item?.defaultAddress);
    },
    isDeliveryAddressStep() {
      return this.stepId === CheckoutStep.DELIVERY_ADDRESS;
    },
    shippingOptionsList() {
      let shippingOptions = {};
      for (const data in this.shipmentsInfo?.shipments) {
        shippingOptions = this.shipmentsInfo.shipments[data].shippingOptions;
      }
      return shippingOptions;
    },
  },

  watch: {
    errorMessages(value) {
      if (!value) return;
      scrollToTop();
    },
  },

  created() {
    // Sets the entries to the checkout store for Multi steps
    this.setupCartData();
  },

  mounted() {
    document.addEventListener(BILLING_DETAILS_EVENT, this.handleBillingErrors);
    if (this.isUserLoggedIn && this.isDeliveryAddressStep) this.populateDeliveryAddressForm();
    this.loadShipmentsOptions();
  },
  unmounted() {
    document.removeEventListener(BILLING_DETAILS_EVENT, this.handleBillingErrors);
  },

  methods: {
    ...mapMutations('checkout', ['setB2cCartData', 'setB2cCartDataFull', 'setIsGr4vyFieldValid', 'setBillingAddress']),
    ...mapActions('user', ['setSelectedLocationFromPostCode']),
    ...mapActions('checkout', ['initializePayment', 'getIsGr4vyPaymentFormValid']),
    ...mapActions('shipments', ['updateShipmentsDetails']),
    ...mapMutations('shipments', ['updateShipmentInfo', 'setShipmentsInfo']),
    /**
     * Handles setting the entries into the checkout store.
     */
    setupCartData() {
      if (!this.cartData) return;

      this.setB2cCartData(this.cartData);
      this.setB2cCartDataFull(this.cartDataFull);
    },

    loadShipmentsOptions() {
      this.setShipmentsInfo(this.shipmentInfos);
      this.selectedOption = this.shippingOptionsList.selected;
      this.updateShipmentInfo(this.selectedOption);
    },
    async handleCheckoutSubmitButtonAction() {
      this.trackBillingCheckoutSteps();

      const isValid = await this.v$.$validate();
      if (!isValid) {
        focusElement(document.querySelector('.has-error'));
        return;
      }
      if (this.selectedPaymentProvider?.value === PaymentProvider.GR4VY) {
        const isGr4vyValid = await this.handleGr4vyValidation();
        if (!isGr4vyValid) return;
      }
      if (this.isRestrictedToRetailer) {
        await ShipmentsApi.updateShipmentsInfo(this.shipmentsInfo.shipments);
      }

      this.initializePayment();
    },
    async handleGr4vyValidation() {
      const isGr4vyPaymentFormValid = await this.getIsGr4vyPaymentFormValid();
      if (!isGr4vyPaymentFormValid) {
        this.$notify({
          type: PdlToastType.ERROR,
          message: this.$t('text.allFieldsAreRequired'),
          showClose: true,
        });
      }
      if (isGr4vyPaymentFormValid) return true;
      Object.keys(this.gr4vyFormValidations).forEach((field) => {
        if (this.gr4vyFormValidations[field].valid === null) {
          this.setIsGr4vyFieldValid({id: field, valid: false, dirty: true});
        }
      });
      scrollToTop();
      return false;
    },
    handleBillingErrors(event) {
      if (!event.detail) return;
      this.setValidationErrors(event.detail);
    },
    clearValidationErrors() {
      this.validationErrors.splice(0);
    },
    setValidationErrors(errorFields) {
      if (!errorFields) return;
      Object.entries(errorFields).forEach(([key, value]) => {
        const parsedKey = parseBillingErrorKey(key);

        const error = {
          field: parsedKey,
          msg: value,
        };

        this.validationErrors.push(error);
      });
      this.$nextTick(() => {
        focusElement(document.querySelector('.has-error'));
      });
    },

    populateDeliveryAddress(customerDetails) {
      const payload = {...this.billingAddress};
      if (this.defaultDeliveryAddress) {
        Object.entries(customerDetails).map(([key, value]) => {
          let deliveryAddressValue = this.defaultDeliveryAddress[value];
          if (key === 'regionIso') {
            deliveryAddressValue = deliveryAddressValue?.isocode;
          }
          payload[key] = deliveryAddressValue;
        });
        this.setBillingAddress(payload);
      }
    },

    populateDeliveryAddressForm() {
      const hasPreferredAddress = Object.keys(RequiredAddressKeyMap).every((key) => this.billingAddress[key]);
      hasPreferredAddress
        ? this.populateDeliveryAddress(PersonalDetailsKeyMap)
        : this.populateDeliveryAddress(CustomerDetailsFieldsKeyMap);
    },
  },
  render: () => null,
};
</script>
